import { ChangeDetectionStrategy, Component, NgModule } from '@angular/core';
import { FormlyModule } from '@ngx-formly/core';
import { FormFieldModule } from '../wrapper/form-field/form-field.component';
import { SignaturePadComponent } from '../reactive-control';
import { AsyncPipe, KeyValuePipe, NgClass, NgForOf, NgIf } from '@angular/common';
import { GENERIC_FILE_CONTROL_DEPENDS, GenericFileControlDirective } from '../directive';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { PreviewFileComponent, UploadFileControlComponent } from '../component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { CyTestDirective } from '@portal/directive';
import { FormlySelectModule, FormlySelectOption } from '@ngx-formly/core/select';
import { ControlComponent } from '@portal/upload-file/control';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'portal-form-signature',
  standalone: true,
  imports: [
    ...GENERIC_FILE_CONTROL_DEPENDS,
    NgForOf,
    KeyValuePipe,
    SignaturePadComponent,
    ReactiveFormsModule,
    FormlyModule,
    PreviewFileComponent,
    NgIf,
    MatCheckboxModule,
    CyTestDirective,
    AsyncPipe,
    FormlySelectModule,
    NgClass,
    ControlComponent,
    MatIconModule,
    UploadFileControlComponent,
  ],
  template: `
    <ng-container *ngFor="let kv of control.value | keyvalue">
      <portal-form-preview-file
        [isDisabled]="disabled"
        [fileModel]="kv.value"
        (remove)="removeFile($event)"
      ></portal-form-preview-file>
    </ng-container>

    <div
      *ngIf="!disabled && !value"
      class="field-button-group"
    >
      <div
        *ngFor="let option of defaultOptions.props.options; let i = index"
        [tabindex]="props.tabindex"
        [portalCyTest]="field.key + '_button'"
        class="field-button"
        [ngClass]="{ 'field-button-selected': signatureMethod.value === option.value }"
      >
        <input
          type="radio"
          class="field-button-input peer"
          [id]="id + '_button_' + i"
          [attr.value]="option.value"
          [value]="option.value"
          [formControl]="signatureMethod"
        />
        <label
          class="field-button-label"
          [for]="id + '_button_' + i"
        >
          {{ option.label }}
        </label>
      </div>
    </div>

    <portal-form-signature-pad
      *ngIf="!disabled && !value && signatureMethod.value === 'draw'"
      [formControl]="control"
      [portalCyTest]="field.key"
      [formlyAttributes]="field"
    ></portal-form-signature-pad>

    <portal-form-upload-file-control
      *ngIf="!disabled && !value && signatureMethod.value === 'upload'"
      [id]="id"
      [showError]="showError"
      [control]="control"
      [acceptMineTypes]="acceptMimeTypes"
      [props]="props"
      [field]="field"
    ></portal-form-upload-file-control>

    <div
      *ngIf="disabled && !value"
      class="field-value"
    >
      -
    </div>
  `,
  styles: [':host {display: block;}'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SignatureComponent extends GenericFileControlDirective {
  protected readonly signatureMethod = new FormControl<'draw' | 'upload'>('draw');

  override readonly defaultOptions = {
    props: {
      options: <FormlySelectOption[]>[
        {
          value: 'draw',
          label: 'Draw Signature',
        },
        {
          value: 'upload',
          label: 'Upload Signature',
        },
      ],
    },
  };

  /**
   * Check if form or field is disabled
   * @returns boolean
   * @readonly
   */
  get disabled(): boolean {
    return !!this.options?.formState?.disabled || !!this.props?.disabled;
  }

  protected get value() {
    return this.control?.value && this.control?.value?.size;
  }
}

@NgModule({
  imports: [
    FormFieldModule,
    FormlyModule.forChild({ types: [{ name: 'signature', component: SignatureComponent, wrappers: ['form-field'] }] }),
  ],
})
export class FormSignatureModule {}
